import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageSquare, Plus, Loader2, Send } from "lucide-react";
import { documentAPI } from "@/lib/api";

export function SendHistoryDialog({ open, onOpenChange, docId, docNumber }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ recipient: "", comment: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const res = await documentAPI.getSendHistory(docId);
      setHistory(res.data.history || []);
    } catch { toast.error("Erreur chargement historique"); }
    finally { setLoading(false); }
  }, [docId]);

  useEffect(() => { if (open) { load(); setShowForm(false); setForm({ recipient: "", comment: "" }); } }, [open, load]);

  const addNote = async () => {
    if (!form.comment.trim()) { toast.error("Commentaire requis"); return; }
    setSaving(true);
    try {
      await documentAPI.addSendHistory(docId, { type: "manual", recipient: form.recipient, comment: form.comment });
      toast.success("Note ajoutée");
      setForm({ recipient: "", comment: "" });
      setShowForm(false);
      load();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Historique d'envoi — {docNumber}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {history.length === 0 && !showForm && (
              <p className="text-xs text-slate-400 text-center py-6">Aucun envoi enregistré</p>
            )}
            {[...history].reverse().map(h => (
              <div key={h.id} className="flex gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-testid={`history-entry-${h.id}`}>
                <div className="mt-0.5">
                  {h.type === "email" ? (
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center"><Mail size={12} className="text-blue-600" /></div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center"><MessageSquare size={12} className="text-amber-600" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className={`text-[9px] px-1 py-0 ${h.type === "email" ? "border-blue-200 text-blue-700" : "border-amber-200 text-amber-700"}`}>
                      {h.type === "email" ? "Email" : "Note"}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{formatDate(h.date)}</span>
                  </div>
                  {h.recipient && <p className="text-[11px] text-slate-600 font-medium">{h.recipient}</p>}
                  <p className="text-[11px] text-slate-500">{h.comment}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">par {h.sent_by}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add note form */}
        {showForm ? (
          <div className="border-t pt-3 space-y-2">
            <Input placeholder="Destinataire (optionnel)" value={form.recipient} onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))}
              className="text-xs h-8" data-testid="history-recipient-input" />
            <Textarea placeholder="Commentaire (ex: relance telephonique, envoi par courrier...)" value={form.comment}
              onChange={e => setForm(f => ({ ...f, comment: e.target.value }))} className="text-xs min-h-[60px]" data-testid="history-comment-input" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button size="sm" className="text-xs h-7 bg-slate-800" onClick={addNote} disabled={saving} data-testid="history-save-btn">
                {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={11} className="mr-1" />} Enregistrer
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t pt-3">
            <Button variant="outline" size="sm" className="text-xs h-7 w-full" onClick={() => setShowForm(true)} data-testid="history-add-note-btn">
              <Plus size={12} className="mr-1" /> Ajouter une note
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
